import {ScaleLinear, scaleLinear} from 'd3-scale'
import {CSSProperties, FC, memo} from 'react'
import {flat, mapToObj, values} from 'remeda'
import {z} from 'zod'
import {ChartCfg, ChartMeta, SheetsData, ZSheetsToCfg} from './chartMeta.types'
import {ChartComponentProps} from './charts'
import {binForecast, binForecastScale, ForecastBinBar, ForecastBinBarProps, LikelihoodBar} from '../ForecastBar'
import {XMetricLabelProps, XMetricLabelDiv} from '../Labels'
import {extent} from 'd3-array'
import {match, P} from 'ts-pattern'
import {O} from 'ts-toolbelt'
import {useElementSize} from '../../ui'
import {groupMapObj, keyBy, mergeTuples, groupMap, filterObj, keys} from '../../collection'
import {geomOfWholePct} from '../../agg'
import {notNull, zx} from '../../type'
import {MantineThemeColors, useSafeMantineTheme} from '@mantine/core'
import {Fmt} from '../../forecast'
import {ForecastDelta} from './Bars'
const zn = zx.nullish
const zSheets = {
  forecasts: z.object({
    code: z.string().describe('Outcome code'),
    before: zn.num.describe('Likelihood'),
    after: zn.num.describe('Likelihood after'),
    comments: zn.string.describe('Comments')
  }),
  consequences: z.object({
    code: z.string().describe('Code'),
    title: zn.string.describe('Title'),
    format: zn.string.describe('Format'),
    formatShort: zn.string.describe('Format short'),
    scaleGroup: zn.string.describe('Scale group'),
    beforeLabel: zn.string.describe('Before label'),
    beforeLabelShort: zn.string.describe('Before label (short)'),
    afterLabel: zn.string.describe('After label'),
    afterLabelShort: zn.string.describe('After label (short)')
  }),
  scales: z.object({
    scaleGroup: zn.string.describe('Scale group'),
    start: zn.num.describe('Start'),
    end: zn.num.describe('End')
  })
}

type CfgSansSheets = {
  labels: {before?: string; after?: string}
  labelsShort: {before?: string; after?: string}
  colors: {before?: string; after?: string; bars?: string}
  subTitle: string
  binScalePerBar?: boolean
  variant: 'two-bar' | 'delta-line'
}

export type ConsequencesCfg = ChartCfg<ZSheetsToCfg<typeof zSheets>> & CfgSansSheets

export type ConsequencesProps = ChartComponentProps<CfgSansSheets & ConsequencesCfg['sheets']> & {
  groupFilter?: string[]
  showTitles?: boolean
}

const modes = ['before', 'after'] as const
type Mode = (typeof modes)[number]

const defaultSheets = <T extends SheetsData>(sheets: T, names: (keyof T)[]) =>
  Object.fromEntries(names.map(n => [n, sheets[n] ?? []] as [keyof T, any])) as T

const type = 'Consequences'
export const consequencesMeta: () => ChartMeta<ConsequencesProps['chart'], ConsequencesCfg> = () => ({
  type,
  title: 'Consequences',
  View: Consequences,
  edit: {
    initial: () => ({source: '', labels: {}, labelsShort: {}, subTitle: '', colors: {}, variant: 'delta-line'}),
    parser: zSheets,
    cfgToProps: ({sheets, ...cfg}) => ({
      ...defaultSheets(sheets, keys(zSheets)),
      ...cfg
    }),
    cfgSchema: {
      subTitle: {label: 'Subtitle'},
      'labels.before': {label: 'Before label', group: 'label'},
      'labelsShort.before': {label: 'Before label (short)', group: 'label'},
      'labels.after': {label: 'After label', group: 'label'},
      'labelsShort.after': {label: 'After label (short)', group: 'label'},
      'colors.bars': {label: 'Bar color', type: 'color', group: 'color'},
      'colors.before': {label: 'Before color', type: 'color', group: 'color'},
      'colors.after': {label: 'After color', type: 'color', group: 'color'},
      binScalePerBar: {label: 'Color scale per bar', type: 'boolean', group: 'color'},
      variant: {label: 'Variant', type: 'segment', options: [{value: 'two-bar'}, {value: 'delta-line'}]}
    }
  }
})

export const Consequences: FC<ConsequencesProps> = memo(
  ({chart, className, groupFilter, showTitles = true}: ConsequencesProps) => {
    const {colors} = useSafeMantineTheme()
    const {title, subTitle, variant} = chart
    const {width: containerWidth, ref} = useElementSize()
    const barWidth = containerWidth - (variant == 'delta-line' ? 70 : 20)
    const {byConsequence, binScale} = layoutData({chart, width: barWidth, groupFilter, colors})
    return (
      <div id='Consequences' className={`flex w-full max-w-[80em] flex-col gap-8 ${className ?? ''}`}>
        <div>
          <h2>{title}</h2>
          <p className='text-gray-600'>{subTitle}</p>
        </div>
        {byConsequence?.map((group, i) => {
          const {
            before,
            after,
            scale,
            consequence: {format, formatShort},
            labels,
            labelsShort
          } = group

          const labelProps = (m: Mode): XMetricLabelProps => ({
            scale,
            value: group[m].aggValue,
            place: m == 'before' ? 'above' : 'below',
            label: labels[m] ?? '',
            color: chart.colors[m],
            format,
            showTick: true
          })

          const distProps = (m: Mode, isTop: boolean): ForecastBinBarProps => ({
            showValue: false,
            value: group[m].aggValue,
            bins: group.bins[m],
            scaleBinColor: (chart.binScalePerBar ? group.binScale : binScale)
              .copy()
              .range([colors.gray[0], chart.colors[m] ?? colors.primary![7]]),
            scale,
            showScale: m == 'before' && (isTop || format != null),
            axisLabelsPlacement: variant == 'two-bar' ? 'book-ends' : undefined,
            format:
              format == null
                ? Fmt.optionsLikelihood
                : Fmt.getOptions({forecastType: 'amount', numberFormat: format, amountType: 'number', unitLabel: ''})
          })

          const barProps = (m: Mode, isTop: boolean) => ({
            value: {
              color: chart.colors[m] ?? colors.primary![7],
              value: group[m].aggValue,
              label: isTop ? labels[m] : labelsShort[m] ?? labels[m] ?? ''
            },
            format,
            scale
          })

          const isTop = i == 0
          const defaultContainerStyle: CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: 50,
            paddingTop: 5,
            paddingBottom: 10
          }

          return (
            <div key={i} className='flex flex-col items-stretch md:flex-row md:items-center'>
              {showTitles && <h3 className='md:w-1/3 md:pr-6'>{group.title}</h3>}
              <div ref={ref} className='grow'>
                {ref.current &&
                  match(variant)
                    .with('delta-line', P.nullish, () => (
                      <div style={defaultContainerStyle}>
                        <div style={{marginBottom: 10}}>
                          <ForecastBinBar {...distProps('before', isTop)} />
                        </div>
                        <div>
                          <XMetricLabelDiv {...labelProps('before')} />
                        </div>
                        <div>
                          <ForecastDelta
                            from={{value: before.aggValue, label: labels.before ?? '', color: chart.colors.before}}
                            to={{value: after.aggValue, label: labels.after ?? '', color: chart.colors.after}}
                            scale={scale}
                            size='lg'
                            format={format}
                          />
                        </div>
                        <div>
                          <XMetricLabelDiv {...labelProps('after')} />
                        </div>
                        <div style={{marginTop: 10}}>
                          <ForecastBinBar {...distProps('after', isTop)} />
                        </div>
                      </div>
                    ))
                    .with('two-bar', () => (
                      <div style={{...defaultContainerStyle, paddingLeft: 5}}>
                        <div style={{marginBottom: 5}}>
                          <LikelihoodBar {...barProps('before', isTop)} />
                        </div>
                        <div>
                          <ForecastBinBar {...distProps('before', isTop)} />
                        </div>
                        <div style={{marginBottom: 5, marginTop: 15}}>
                          <LikelihoodBar {...barProps('after', isTop)} />
                        </div>
                        <div>
                          <ForecastBinBar {...distProps('after', isTop)} />
                        </div>
                      </div>
                    ))
                    .exhaustive()}
              </div>
            </div>
          )
        })}
      </div>
    )
  }
)
Consequences.displayName = 'Consequences'

type Forecast = ConsequencesProps['chart']['forecasts'][number]

const defaultScaleGroup = '__default__'

const layoutData = ({
  chart,
  width,
  groupFilter,
  colors
}: {
  chart: ConsequencesProps['chart']
  width: number
  groupFilter?: string[]
  colors: MantineThemeColors
}) => {
  const {consequences, labels: chartLabels, labelsShort: chartLabelsShort} = chart
  const consequenceByCode = keyBy(
    consequences
      .filter(c => groupFilter == null || groupFilter.includes(c.code))
      .map(c => ({scaleGroup: defaultScaleGroup, ...c})),
    c => c.code
  )
  const boundsByScaleGroup = keyBy(chart.scales, s => s.scaleGroup ?? defaultScaleGroup)
  const getBoundOverride = (scaleGroup: string): [number | undefined, number | undefined] => {
    const boundsRow = boundsByScaleGroup[scaleGroup ?? defaultScaleGroup]
    if (boundsRow == null) return [undefined, undefined]
    return [boundsRow.start, boundsRow.end]
  }
  const forecasts = chart.forecasts
    .filter(f => f.code && (f.before != null || f.after != null))
    .map(f => ({...f, consequence: consequenceByCode[f.code]!}))
    .filter(f => f.consequence != null)

  const forecastsByScaleGroup = groupMapObj(
    forecasts,
    f => f.consequence.scaleGroup,
    (forecasts, scaleGroup) => {
      const values = flat(forecasts.map(f => [f.before, f.after])).filter(notNull)
      const rawBounds: number[] = chart.variant == 'two-bar' ? [0, extent(values)[1]!] : (extent(values) as number[])
      const withOverride = mergeTuples(rawBounds, getBoundOverride(scaleGroup))
      const scale = scaleLinear(withOverride, [0, width]).nice()
      const bounds = scale.domain()
      return {scale, scaleGroup, bounds}
    }
  )

  const getBar = (forecasts: Forecast[], by: (o: Forecast) => number | undefined) => {
    const forecastsWithValues = forecasts.filter(f => by(f) != null)
    const all = forecastsWithValues.map(by)
    return {
      aggValue: geomOfWholePct(forecastsWithValues, v => by(v)!) ?? 0,
      all
    }
  }

  const byConsequence = groupMap(
    forecasts,
    o => o.code,
    (code, forecasts) => {
      const consequence = consequenceByCode[code]!
      const {scale, bounds} = forecastsByScaleGroup[consequence.scaleGroup]!
      const bins = modeBinForecasts({scale, forecasts})
      const forecastsByMode = mapToObj(modes, m => [m, getBar(forecasts, o => o[m])])
      const binScale = binForecastScale({bins: flat(values(bins)), colors})
      const notEmpty = (_: Mode, v: string | undefined): boolean => v != null && v != ''
      const consequenceLabels = filterObj({before: consequence.beforeLabel, after: consequence.afterLabel}, notEmpty)
      const consequenceLabelsShort = filterObj(
        {before: consequence.beforeLabelShort, after: consequence.afterLabelShort},
        notEmpty
      )
      const labels = {...filterObj(chartLabels, notEmpty), ...consequenceLabels}
      const labelsShort = {
        ...filterObj(labels, notEmpty),
        ...filterObj(chartLabelsShort, notEmpty),
        ...consequenceLabelsShort,
        ...consequenceLabels
      }
      return {
        code,
        title: consequence.title ?? code,
        forecasts,
        bins,
        binScale,
        ...forecastsByMode,
        labels,
        labelsShort,
        scale,
        bounds,
        consequence
      }
    }
  ).filter(g => g.forecasts.length)

  // global bin scale
  const binScale = binForecastScale({
    bins: flat(values(byConsequence))
      .flatMap(o => o.bins)
      .flatMap(b => [...b.before, ...b.after])
      .filter(notNull),
    colors
  })
  return {byConsequence, binScale}
}

const nonEmptyForecast = <T extends Mode>(forecasts: Forecast[], m: T) =>
  forecasts.filter(f => f[m] != null) as any as O.Required<Forecast, T>[]

const modeBinForecasts = ({scale, forecasts}: {scale: ScaleLinear<number, number>; forecasts: Forecast[]}) =>
  mapToObj(modes, m => [
    m,
    binForecast({
      domain: scale,
      forecasts: nonEmptyForecast(forecasts, m).map(o => ({value: o[m], comments: o.comments?.split('|')}))
    }).bins
  ])
